import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { AUTH } from "@/constants/testIds";
import { BrandLogo } from "@/components/BrandLogo";
import { landingForUser } from "@/lib/roleRedirect";

// Friendly copy for backend ?social_error= codes.
const SOCIAL_ERRORS = {
  cancelled: "Sign-in was cancelled — you can try again below.",
  invalid_state: "That sign-in attempt expired. Please try again.",
  provider_error: "The sign-in provider is temporarily unavailable. Please try again, or sign in with your email and password.",
  no_email: "We couldn't get a verified email from that provider. Sign in with your email and password first, then connect it from Account → Sign-in methods.",
  ambiguous: "We couldn't safely match that email to a single AWARE account. Please sign in with your email and password, or contact support.",
  pending_approval: "Your account is awaiting admin approval.",
  account_blocked: "This account is not able to sign in. Please contact support.",
  not_configured: "That sign-in method isn't available right now.",
};

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#1877F2" d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07c0 6.02 4.39 11.02 10.13 11.93v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.7 4.53-4.7 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.95.93-1.95 1.89v2.26h3.32l-.53 3.49h-2.79V24C19.61 23.09 24 18.09 24 12.07z"/>
  </svg>
);

export default function UnifiedLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [providers, setProviders] = useState({ google: false, facebook: false });
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionExpired = params.get("expired") === "1";
  const socialError = SOCIAL_ERRORS[params.get("social_error")] || null;

  const nextPath = useMemo(() => {
    const n = params.get("next") || "";
    return n.startsWith("/") && !n.startsWith("//") ? n : null;
  }, [params]);

  useEffect(() => {
    // Unconfigured providers stay hidden — no dead buttons for customers.
    api.get("/auth/providers").then(({ data }) => setProviders(data)).catch(() => {});
  }, []);

  const startSocial = (provider) => {
    const q = nextPath ? `?next=${encodeURIComponent(nextPath)}` : "";
    window.location.assign(`${API_BASE}/auth/social/${provider}/start${q}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.full_name.split(" ")[0]}`);
      navigate(nextPath || landingForUser(user));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const anySocial = providers.google || providers.facebook;

  return (
    <div data-testid="unified-login-page" className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <BrandLogo to="/" size="sm" />
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div data-testid="login-kicker" className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-600">Sign In</div>
            <h1 className="font-display mt-2 text-3xl lg:text-4xl font-bold text-slate-900">Welcome to AWARE</h1>
            <p className="mt-2 text-slate-600">Access your water testing results, bookings and account.</p>
          </div>

          {sessionExpired && (
            <div data-testid="login-session-expired-banner" className="mb-4 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800 font-medium">
              Your session has expired — please sign in again to continue where you left off.
            </div>
          )}
          {socialError && (
            <div data-testid="login-social-error-banner" className="mb-4 rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700 font-medium">
              {socialError}
            </div>
          )}

          <div className="bg-white rounded-3xl border border-slate-200 p-7 shadow-sm">
            {anySocial && (
              <div className="space-y-3 mb-5">
                {providers.google && (
                  <button data-testid="login-google-btn" type="button" onClick={() => startSocial("google")}
                    className="w-full h-12 flex items-center justify-center gap-3 rounded-full border border-slate-300 bg-white font-semibold text-slate-800 hover:bg-slate-50 transition-colors">
                    <GoogleIcon /> Continue with Google
                  </button>
                )}
                {providers.facebook && (
                  <button data-testid="login-facebook-btn" type="button" onClick={() => startSocial("facebook")}
                    className="w-full h-12 flex items-center justify-center gap-3 rounded-full border border-slate-300 bg-white font-semibold text-slate-800 hover:bg-slate-50 transition-colors">
                    <FacebookIcon /> Continue with Facebook
                  </button>
                )}
                <div className="flex items-center gap-3 pt-1">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-[11px] uppercase tracking-wider font-bold text-slate-400">or</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
              </div>
            )}

            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Email</Label>
                <Input data-testid={AUTH.loginEmail} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-1.5 h-12 rounded-xl" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Password</Label>
                <Input data-testid={AUTH.loginPassword} type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5 h-12 rounded-xl" />
              </div>
              <Button data-testid={AUTH.loginSubmit} disabled={submitting} type="submit" size="lg" className="w-full btn-aqua rounded-full font-semibold mt-1">
                {submitting ? "Signing in..." : <>Sign in <ArrowRight className="w-4 h-4 ml-1.5" /></>}
              </Button>
              <p className="text-center text-xs text-slate-500 pt-1">
                <Link to="/forgot-password" data-testid="login-forgot-password-link" className="font-semibold text-slate-600 hover:text-cyan-700 hover:underline">
                  Forgot your password?
                </Link>
              </p>
            </form>
          </div>

          <p className="mt-5 text-center text-xs text-slate-500">
            We'll automatically take you to the right place after sign-in — customers, partners, lab staff and admins all sign in here.
          </p>
          <p className="mt-4 text-center text-sm text-slate-500">
            New here? <Link to="/customer/register" data-testid="login-register-link" className="font-semibold text-cyan-600 hover:underline">Create a customer account →</Link>
          </p>
          <p className="mt-2 text-center text-xs text-slate-400">
            <Link to="/trade/register" className="font-semibold text-slate-500 hover:underline">Apply for a trade partner account</Link>
            {" · "}
            <Link to="/field/login" className="font-semibold text-slate-500 hover:underline">Technician sign-in</Link>
            {" · "}
            <Link to="/landlord/login" className="font-semibold text-slate-500 hover:underline">Landlord access</Link>
          </p>
        </div>
      </main>
    </div>
  );
}
